import { partnerApi } from './partner-api';
import { companyLedgerApi } from './partner-ledger-api';
import { partnerAccountsApi } from './partner-accounts-api';
import { creditAlertsApi } from './credit-alerts-api';
import { createPaigeClient, readTimeout, type PaigeClient } from '@paige/api-client';
import type { EventSourceLike } from '@paige/live';
import type { DataSource } from './data-source';
import { sessionApi } from './session-api';
import { appearanceApi } from './appearance-api';
import { engagementApi } from './engagement-api';
import { fleetApi } from './fleet-api';
import { companyApi } from './company-api';
import { apiKeysApi } from './api-keys-api';
import { devicesApi } from './devices-api';
import { mobileApi } from './mobile-api';
import { leadsApi } from './leads-api';
import { notificationsApi } from './notifications-api';
import { adminApi } from './admin-api';

export interface ApiDataSourceOptions {
  /** `/v1` behind the same origin (the dev server proxies it), or an absolute API origin. */
  baseUrl: string;
  clientVersion: string;
}

/** The browser's EventSource, with credentials so the session cookie rides along. */
function browserEventSource(url: string): EventSourceLike {
  const source = new EventSource(url, { withCredentials: true });
  const like: EventSourceLike = {
    onopen: null,
    onerror: null,
    onmessage: null,
    close: () => source.close(),
  };
  source.onopen = (ev) => like.onopen?.(ev);
  source.onerror = (ev) => like.onerror?.(ev);
  source.onmessage = (ev: MessageEvent<string>) =>
    like.onmessage?.({ data: ev.data, lastEventId: ev.lastEventId });
  return like;
}

/** The DataSource over the real API contract; every screen reads through this. */
export function createApiDataSource(opts: ApiDataSourceOptions): DataSource {
  const client: PaigeClient = createPaigeClient({
    baseUrl: opts.baseUrl,
    clientVersion: opts.clientVersion,
  });
  client.use(readTimeout());
  return {
    session: sessionApi(client),
    appearance: appearanceApi(client),
    engagement: engagementApi(client),
    fleet: fleetApi(client),
    company: companyApi(client),
    partner: partnerApi(client),
    companyLedger: companyLedgerApi(client),
    partnerAccounts: partnerAccountsApi(client),
    alerts: creditAlertsApi(client),
    keys: apiKeysApi(client),
    devices: devicesApi(client),
    mobile: mobileApi(client),
    leads: leadsApi(client),
    notifications: notificationsApi(client),
    client,
    admin: adminApi(client),
    createEventSource: browserEventSource,
    eventsUrl: (tenant) => `${opts.baseUrl}/events?tenant=${encodeURIComponent(tenant)}`,
  };
}
